import React from "react";

/**
 * Top-level error boundary — catches any React tree crash and renders a
 * friendly fallback so users NEVER see a blank page. Also logs to console
 * so we can diagnose issues in production.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("[Keneth] Runtime crash caught by ErrorBoundary:", error, info?.componentStack);
    }

    handleReset = () => {
        try {
            localStorage.removeItem("kg_cart_v1");
            localStorage.removeItem("kg_wishlist_v1");
        } catch {}
        window.location.href = "/";
    };

    render() {
        if (this.state.error) {
            return (
                <div className="min-h-screen bg-ink-900 flex items-center justify-center px-6" data-testid="error-boundary">
                    <div className="max-w-lg text-center">
                        <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-5">— Something went wrong</p>
                        <h1 className="font-serif text-4xl sm:text-5xl text-bone-100 leading-tight mb-5">
                            The atelier is <em className="italic text-brass-400">catching its breath.</em>
                        </h1>
                        <p className="text-sm sm:text-base text-bone-300 font-light leading-relaxed mb-8">
                            A part of the page didn't load correctly. Try reloading, or return to the home page and start over.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                            <button
                                onClick={() => window.location.reload()}
                                className="border border-brass-400 text-brass-400 hover:bg-brass-400 hover:text-ink-900 transition-colors duration-500 px-6 py-3.5 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase"
                            >
                                Reload page
                            </button>
                            <button
                                onClick={this.handleReset}
                                className="text-bone-300 hover:text-brass-400 transition-colors px-4 py-3.5 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase link-hairline"
                            >
                                Start over
                            </button>
                        </div>
                        <details className="mt-10 text-left text-[10px] text-bone-300/50 tracking-wide">
                            <summary className="cursor-pointer text-bone-300/70 uppercase tracking-[0.28em] text-[10px] pb-2">Technical detail</summary>
                            <pre className="whitespace-pre-wrap break-words max-h-40 overflow-y-auto p-3 bg-ink-800/50 border border-white/5">
                                {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
                            </pre>
                        </details>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
